<template>
  <section>
    <b-card-actions :show-loading="parsing" action-collapse :title="$t('Arquivo de conciliação')">
      <FormulateForm ref="form">
        <b-row>
          <b-col md="8">
            <FormulateInput
              id="reconciliation-file-parser-file"
              v-model="file"
              type="uploader"
              max-files="1"
              :theme="'list'"
              :deletable="true"
              :meta="true"
              :label="$t('Arquivo')"
              :help-text="
                $t(
                  'Arraste o arquivo de conciliação Getnet ou Pix aqui (ou clique para selecionar o arquivo)'
                )
              "
              @input="onFileChanged"
              @delete="onFileRemoved"
            />
          </b-col>
          <b-col v-if="result" md="4">
            <FormulateInput
              id="reconciliation-file-parser-layout"
              v-model="layout"
              type="vue-select"
              :clearable="false"
              :label="$t('Layout')"
              :placeholder="$t('Selecione o layout do arquivo')"
              :options="layoutOptions"
              @input="onLayoutChanged"
            />
            <b-badge v-if="result.layoutDetected" variant="light-success">
              {{ $t('Identificado automaticamente') }}
            </b-badge>
          </b-col>
        </b-row>
      </FormulateForm>

      <b-alert v-if="result && !result.layout" show variant="warning" class="mt-1">
        <div class="alert-body">
          {{
            $t(
              'Não foi possível identificar o layout do arquivo automaticamente. Selecione o layout para fazer a leitura.'
            )
          }}
        </div>
      </b-alert>
    </b-card-actions>

    <b-card-actions v-if="result && result.layout" action-collapse :title="$t('Resumo')">
      <b-row>
        <b-col md="4">
          <FormulateInput
            id="reconciliation-file-parser-file-name"
            :value="result.fileName"
            type="label"
            :label="$t('Arquivo')"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="reconciliation-file-parser-layout-name"
            :value="layoutDescription"
            type="label"
            :label="$t('Layout')"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="reconciliation-file-parser-total-lines"
            :value="result.totalLines"
            type="label"
            :label="$t('Linhas no arquivo')"
          />
        </b-col>
      </b-row>
      <b-alert v-if="result.warnings.length" show variant="warning" class="mt-1 mb-0">
        <div class="alert-body">
          <ul class="mb-0 pl-1">
            <li v-for="(warning, index) in result.warnings" :key="index">
              {{ warning }}
            </li>
          </ul>
        </div>
      </b-alert>
    </b-card-actions>

    <b-card v-if="result && result.layout">
      <b-tabs v-model="activeTab">
        <b-tab v-for="section in sections" :key="section.registryType">
          <template #title>
            {{ section.name }}
            <b-badge variant="light-primary" class="ml-50">
              {{ section.rows.length }}
            </b-badge>
            <b-badge
              v-if="section.errorCount"
              variant="light-danger"
              class="ml-25"
              :title="$t('Linhas com erro de leitura')"
            >
              {{ section.errorCount }}
            </b-badge>
          </template>

          <b-row class="mb-1">
            <b-col md="6">
              <b-form-input
                v-model="filterText"
                type="search"
                :placeholder="$t('Filtrar linhas...')"
              />
            </b-col>
            <b-col md="6" class="d-flex align-items-center justify-content-md-end">
              <b-form-checkbox v-if="section.errorCount" v-model="onlyErrors" switch class="mr-2">
                {{ $t('Somente linhas com erro') }}
              </b-form-checkbox>
              <b-form-checkbox v-if="section.mapped" v-model="showRaw" switch>
                {{ $t('Exibir linha original') }}
              </b-form-checkbox>
            </b-col>
          </b-row>

          <p v-if="!section.mapped" class="text-muted">
            {{
              $t(
                'Este tipo de registro não tem layout mapeado no sistema. As linhas são exibidas como estão no arquivo.'
              )
            }}
          </p>

          <b-table
            show-empty
            responsive
            striped
            small
            class="bordered reconciliation-file-table"
            :empty-text="$t('Nenhuma linha encontrada')"
            :fields="section.fields"
            :items="section.items"
            :filter="filterText || null"
            :current-page="currentPage"
            :per-page="perPage"
            :tbody-tr-class="rowClass"
            @filtered="onFiltered"
          >
            <template #cell(error)="{ item }">
              <span v-if="item.error" class="text-danger">
                {{ item.error }}
              </span>
            </template>
            <template #cell(raw)="{ item }">
              <span class="raw-line">{{ item.raw }}</span>
            </template>
            <template #cell()="{ item, field, value }">
              <span
                :class="{
                  'text-danger font-weight-bold': item.invalidColumns.includes(field.columnIndex),
                }"
                :title="cellTitle(item, field)"
              >
                {{ value }}
              </span>
            </template>
          </b-table>

          <b-row class="pt-1">
            <b-col cols="6" class="d-flex align-items-center">
              <e-page-size-selector :per-page="perPage" @change="onPageSizeChange" />
              <span class="ml-1 text-muted"> {{ filteredCount }} {{ $t('linha(s)') }} </span>
            </b-col>
            <b-col cols="6">
              <b-pagination
                v-model="currentPage"
                align="right"
                :total-rows="filteredCount"
                :per-page="perPage"
              />
            </b-col>
          </b-row>
        </b-tab>
      </b-tabs>
    </b-card>
  </section>
</template>

<script>
import {
  BAlert,
  BBadge,
  BCard,
  BCol,
  BFormCheckbox,
  BFormInput,
  BPagination,
  BRow,
  BTab,
  BTable,
  BTabs,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector } from '@/views/components'
import { uploader } from '@/mixins'

export default {
  components: {
    BAlert,
    BBadge,
    BCard,
    BCardActions,
    BCol,
    BFormCheckbox,
    BFormInput,
    BPagination,
    BRow,
    BTab,
    BTable,
    BTabs,
    EPageSizeSelector,
  },

  mixins: [uploader],

  data() {
    return {
      file: [],
      layout: null,
      result: null,
      parsing: false,
      activeTab: 0,
      filterText: '',
      onlyErrors: false,
      showRaw: false,
      currentPage: 1,
      perPage: 50,
      filteredCount: 0,
    }
  },

  computed: {
    layoutOptions() {
      return (this.result?.availableLayouts || []).map(option => ({
        value: option.layout,
        label: option.description,
      }))
    },

    layoutDescription() {
      return this.layoutOptions.find(option => option.value === this.result?.layout)?.label
    },

    sections() {
      return (this.result?.sections || []).map(section => ({
        ...section,
        name: `${section.name} (${this.$t('tipo')} ${section.registryType})`,
        fields: this.buildFields(section),
        items: this.buildItems(section),
      }))
    },
  },

  watch: {
    activeTab() {
      this.resetTableState()
    },
    onlyErrors() {
      this.currentPage = 1
      this.updateFilteredCount()
    },
  },

  methods: {
    async parseFile(layout) {
      if (!this.file?.length) return

      try {
        this.parsing = true
        const params = layout ? { layout } : {}
        this.result = await this.uploadCustom({
          file: this.file[0],
          apiUrl: '/api/financial/reconciliation/file-parser',
          params,
          fileParamName: 'file',
        })
        this.layout = this.result.layout
        this.activeTab = 0
        this.resetTableState()
      } catch (error) {
        this.showError({
          error,
          message: this.$t('Não foi possível fazer a leitura do arquivo'),
        })
      } finally {
        this.parsing = false
      }
    },

    onFileChanged() {
      this.result = null
      this.layout = null
      this.parseFile(null)
    },

    onFileRemoved() {
      this.file = []
      this.result = null
      this.layout = null
    },

    onLayoutChanged(layout) {
      if (!layout || layout === this.result?.layout) return
      this.parseFile(layout)
    },

    buildFields(section) {
      const fields = [
        {
          key: 'line',
          label: this.$t('Linha'),
          tdClass: 'text-center',
          thStyle: { width: '70px' },
        },
      ]

      if (section.errorCount) {
        fields.push({ key: 'error', label: this.$t('Erro'), thStyle: { minWidth: '250px' } })
      }

      section.columns.forEach((column, columnIndex) => {
        fields.push({
          key: `c${columnIndex}`,
          label: column.name,
          columnIndex,
          column,
          thAttr: {
            title: `${this.$t('Posição')} ${column.start}-${column.start + column.length - 1} | ${
              column.type
            }${column.format ? ` (${column.format})` : ''}`,
          },
          tdClass: 'text-nowrap',
        })
      })

      if (!section.mapped || this.showRaw) {
        fields.push({ key: 'raw', label: this.$t('Linha original') })
      }

      return fields
    },

    buildItems(section) {
      const items = section.rows.map(row => {
        const item = {
          line: row.lineNumber,
          raw: row.raw,
          error: row.error,
          invalidColumns: row.invalidColumns || [],
        }
        row.values.forEach((value, index) => {
          item[`c${index}`] = value
        })
        return item
      })

      return this.onlyErrors ? items.filter(item => item.error) : items
    },

    rowClass(item) {
      return item?.error ? 'table-danger' : null
    },

    cellTitle(item, field) {
      const { column } = field
      if (!column) return null
      const raw = item.raw.substr(column.start - 1, column.length)
      return `${this.$t('Posição')} ${column.start}-${column.start + column.length - 1} | ${this.$t(
        'Valor no arquivo'
      )}: "${raw}"`
    },

    onFiltered(filteredItems) {
      this.filteredCount = filteredItems.length
      this.currentPage = 1
    },

    onPageSizeChange(pageSize) {
      this.perPage = pageSize
      this.currentPage = 1
    },

    resetTableState() {
      this.filterText = ''
      this.onlyErrors = false
      this.currentPage = 1
      this.updateFilteredCount()
    },

    updateFilteredCount() {
      this.filteredCount = this.sections[this.activeTab]?.items.length || 0
    },
  },
}
</script>

<style lang="scss" scoped>
.reconciliation-file-table {
  font-size: 0.85rem;

  .raw-line {
    font-family: monospace;
    white-space: pre;
  }
}
</style>
